import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { FixturePage } from '@cds/betting-offer';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { QuickDepositConfig } from '@frontend/sports/common/client-config-data-access';
import { IPayloadAction } from '@frontend/sports/common/core/utils/redux';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { ConfirmationDialogButtonType } from '@frontend/sports/modal/feature';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { EarlyPayoutAcceptance, SyncUserSettingsService } from '@frontend/sports/user/feature';
import { CashierService, WINDOW } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store, createSelector } from '@ngrx/store';
import { values } from 'lodash-es';
import { AdaptiveLayoutService } from 'packages/sports/web/app/src/layout/adaptive-layout.service';
import { EarlyPayoutErrors } from 'packages/sports/web/app/src/my-bets-base/models/early-payout-types';
import { UiManagerActions } from 'packages/sports/web/app/src/ui-manager/ui-manager.actions';
import { IUiState, uiStateSelector } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { hasUiState } from 'packages/sports/web/app/src/ui-manager/ui-state-helper';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { Observable, catchError, concatMap, filter, from, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipSourcePageActions } from '../../../core/betslip-source-page.actions';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipType } from '../../../core/betslip-type';
import { ExternalBetslipActions } from '../../../core/external-betslip-actions';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { V1PickId, V2OptionMarketPickId, V2OptionMarketXCastPickId, V2ParticipantPickId } from '../../../core/picks/pick-id';
import { ResultStatus } from '../../../core/picks/pick-models';
import { EditMyBetPicksPayload } from '../../../model/edit-mybet/edit-bet-init';
import { OverAskFlowState } from '../../../model/over-ask/over-ask';
import { BetslipDialogFullMode, BetslipDialogFullService } from '../../common/services/betslip-dialog-full.service';
import { BetslipNotificationActions } from '../../notifications/betslip-notification.actions';
import { overAskFlowStateSelector } from '../../over-ask/selectors';
import BetslipPickActions from '../../picks/actions';
import BetslipPickFactory from '../../picks/factory/betslip-pick.factory';
import { BetslipPicksRequestBuilderService } from '../../picks/services/betslip-pick-request-builder.service';
import { QuickDepositActions } from '../../quick-deposit/quick-deposit-actions';
import { QuickDepositService } from '../../quick-deposit/services/quick-deposit.service';
import { RewardTokensActions } from '../../reward-tokens/actions';
import { BetslipTypesActions } from '../../types/actions';
import { betslipTypeStateSelector, editBetPickStateSelector, editBetPicksListSelector, editBetStateSelector } from '../../types/selectors';
import BetslipErrorsActions from '../../validation/actions';
import { SimpleBetslipNotification } from '../../validation/errors/betslip-notification';
import { ResultStateChanged } from '../../validation/errors/edit-bet/result-state-changed';
import { NotEnoughMoney } from '../../validation/errors/user/not-enough-money';
import { selectNotEnoughMoneyError } from '../../validation/selectors';
import { EditBetActions, IEditBetPickResultsPayload, IFillEditMyBetPayload, ISwapPickPayload } from '../actions';
import { pickResultedStateFromEarlyPayoutResultedPickState } from '../actions-apply';
import { EditBetOnBoardingService } from '../services/betslip-onboarding.service';
import { EditBetBetLostDialogService } from '../services/dialogs/edit-bet-bet-lost-dialog.service';
import { EditBetBetSettledDialogService } from '../services/dialogs/edit-bet-bet-settled-dialog.service';
import { EditBetBetWonDialogService } from '../services/dialogs/edit-bet-bet-won-dialog.service';
import { EditBetExitConfirmationDialogService } from '../services/dialogs/edit-bet-exit-confirmation-dialog.service';
import { EditBetPicksAddedInfoDialogService } from '../services/dialogs/edit-bet-picks-added-info-dialog.service';
import { EditBetV2PickAddConfirmationDialogService } from '../services/dialogs/edit-bet-v2-pick-add-confirmation-dialog.service';
import { EditBetCheckingService } from '../services/edit-bet-checking.service';
import { EditBetConvertersService } from '../services/edit-bet-converters.service';
import { EditBetPickRefreshService } from '../services/edit-bet-pick-refresh.service';
import { EditBetPicksResultedState, getEditBetResultedState } from '../services/edit-bet-utils';

const selectEditBetPicks = createSelector(editBetPicksListSelector, editBetPickStateSelector, (pickList, pickState) => ({
    pickList,
    pickState,
}));

@Injectable()
export class BetslipEditMybetEffects {
    private readonly _doc = inject(DOCUMENT);
    private readonly _window = inject(WINDOW);

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private cashierService: CashierService,
        private quickDepositService: QuickDepositService,
        private bettingOffer: BettingOfferApi,
        private pickFactory: BetslipPickFactory,
        private editMyBetConvertersService: EditBetConvertersService,
        private editMyBetCheckingService: EditBetCheckingService,
        private betslipDialogFullService: BetslipDialogFullService,
        private editBetExitConfirmationDialogService: EditBetExitConfirmationDialogService,
        private editBetV2PickAddConfirmationDialogService: EditBetV2PickAddConfirmationDialogService,
        private editBetPicksAddedInfoDialogService: EditBetPicksAddedInfoDialogService,
        private betslipPicksRequestBuilder: BetslipPicksRequestBuilderService,
        private timerService: TimerService,
        private adaptiveLayoutService: AdaptiveLayoutService,
        private editBetBetWonDialogService: EditBetBetWonDialogService,
        private editBetBetLostDialogService: EditBetBetLostDialogService,
        private editBetBetSettledDialogService: EditBetBetSettledDialogService,
        private refreshPickService: EditBetPickRefreshService,
        private trackingService: TrackingService,
        private onboardingService: EditBetOnBoardingService,
        private quickDepositConfig: QuickDepositConfig,
        private userSettings: SyncUserSettingsService,
    ) {}

    /**
     * Handle start edit bet action. Creates 3 more actions
     * 1. Set UI State to Edit bet.
     * 2. Fill edit bet with data from my bets.
     * 3. Set Betslip state to EDIT.
     * Remarks: EditBet is not allowed while in OverAsk mode
     */
    handleStartEditMyBetEpicEffect$ = createEffect(() => {
        const epic = this;

        return this.actions$.pipe(
            ofType(ExternalBetslipActions.startEditMyBet),
            withLatestFrom(this.store.select(overAskFlowStateSelector)),
            filter(([_, overAskFlowState]) => overAskFlowState === OverAskFlowState.None),
            map(([action, _]) => action),
            withLatestFrom(this.store.select(uiStateSelector)),
            concatMap(function* ([action, uiState]): Iterable<Action> {
                yield EditBetActions.init({ payload: action.payload });
                yield BetslipTypesActions.setOriginalBase();
                yield BetslipTypesActions.setActiveTypes({ betslipTypes: [BetslipType.EditBet] });
                yield BetslipTypesActions.setSelectedType({
                    betslipType: BetslipType.EditBet,
                    selectedByUser: false,
                    systemInfo: null,
                });
                yield BetslipSourcePageActions.setSourcePage({ payload: action.payload.sourcePageState });
                if (uiState.states.indexOf(UiStates.EditBetResult) > -1) {
                    yield UiManagerActions.removeState({ state: UiStates.EditBetResult });
                }

                yield UiManagerActions.addState({ state: UiStates.EditBet });
                yield epic.convertStartEditMyBet(action);
                yield BetslipActions.setState({ state: BetslipState.Edit });
                yield RewardTokensActions.deselectAllToken();
            }),
        );
    });

    /**
     * Handle user cancel edit bet request.
     * If no changes: in the edit bet - Exits.
     * If has changes: Shows confirmation dialog and if user confirms exits.
     */
    handleDiscardRequestEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.requestDiscard),
            withLatestFrom(this.store.select(betslipTypeStateSelector)),
            concatMap(([action, types]) => {
                if (this.editMyBetCheckingService.hasChanges(types)) {
                    return from(this.editBetExitConfirmationDialogService.show()).pipe(
                        map((val) => {
                            if (val === ConfirmationDialogButtonType.Cancel) {
                                this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                                    [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_EditBetExit_Screen_ExitLoseChanges_Clicked',
                                    [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                                });

                                return EditBetActions.exitEditBet();
                            } else {
                                this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                                    [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_EditBetExit_Screen_ContinueEditBet_Clicked',
                                    [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                                });

                                return EditBetActions.cancelDiscardRequest();
                            }
                        }),
                        catchError(() => {
                            return of(EditBetActions.cancelDiscardRequest());
                        }),
                    );
                }

                return [EditBetActions.exitEditBet()];
            }),
        );
    });

    /**
     * Handle pressing deposit on NotEnoughMoneyError
     * Ask for confirmation before leaving EMB
     * If user exits: leave, then redirect to deposit page
     */
    handleMakeDepositEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.makeDeposit),
            withLatestFrom(this.store.select(selectNotEnoughMoneyError)),
            mergeMap(([action, notEnoughMoneyError]) => {
                if (!this.quickDepositConfig.loadQuickDepositConfigOnLogin) {
                    return from(
                        this.quickDepositService.isQuickDepositEnabled().pipe(
                            concatMap((_) => {
                                return this.loadQuickDeposit(notEnoughMoneyError);
                            }),
                        ),
                    );
                } else {
                    return this.loadQuickDeposit(notEnoughMoneyError);
                }
            }),
        );
    });

    handleQuickDepositEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.deposit),
            tap(() => this.store.dispatch(QuickDepositActions.depositProcessing({ processing: true }))),
            concatMap(() =>
                from(this.quickDepositService.deposit()).pipe(
                    concatMap((_) => {
                        return of(QuickDepositActions.depositProcessing({ processing: false }));
                    }),
                ),
            ),
        );
    });

    /**
     * Handle exit edit bet action.
     * Removes all edit bet ui states.
     */
    handleExitEditBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.exitEditBet),
            tap(() => {
                this.refreshPickService.cleanSchedules();
                this.onboardingService.closeDialog();
            }),
            concatMap(function* (): Iterable<Action> {
                yield UiManagerActions.removeMultiStates({
                    states: [
                        UiStates.EditBetAddSelection,
                        UiStates.EditBetAddStake,
                        UiStates.EditBetSwapSelection,
                        UiStates.EditBet,
                        UiStates.EditBetResult,
                    ],
                });
                yield BetslipTypesActions.setFromOriginalBase();
                yield EditBetActions.setDefault();
            }),
        );
    });

    /**
     * Handles user desire to add more picks.
     * If currently swap dialog opened closes it.
     */
    handleSwitchToAddSelectionEffect$ = createEffect(() => {
        const epic = this;

        return this.actions$.pipe(
            ofType(EditBetActions.switchToAddSelectionMode),
            withLatestFrom(this.store.select(uiStateSelector)),
            concatMap(function* ([_, uiState]: [Action, IUiState]): Iterable<Action> {
                epic.scrollToSummary(uiState);
                if (hasUiState(uiState, UiStates.EditBetSwapSelection)) {
                    yield EditBetActions.cancelSwapSelectionMode();
                }
                yield UiManagerActions.addState({ state: UiStates.EditBetAddSelection });
            }),
        );
    });

    /**
     * Handle adding pick to staging area.
     * Scrolls to edit bet summary
     */
    handleAddPickToContainerActionEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(EditBetActions.addPickToContainer),
                withLatestFrom(this.store.select(uiStateSelector)),
                tap(([_, uiState]) => {
                    this.scrollToSummary(uiState);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * Handle canceling of adding pick. Closes staging area.
     */
    handleCancelAddSelectionEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.cancelAddSelectionMode),
            map(() => {
                return UiManagerActions.removeState({ state: UiStates.EditBetAddSelection });
            }),
        );
    });

    /**
     * Handle Adding picks from staging area.
     * Adds staging area picks to edit bet.
     */
    handleConfirmAddSelectionEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.confirmAddSelectionMode),
            withLatestFrom(this.store.select(selectEditBetPicks)),
            concatMap(([action, { pickList, pickState }]) => {
                const picks = values(action.payload.picks);

                let actions: Action[] = [UiManagerActions.removeState({ state: UiStates.EditBetAddSelection })];
                if (picks.length > 0) {
                    const pickIds = picks.length > 1 ? picks.map((p) => p.id) : [picks[0].id];

                    const pickActions = picks.reduce((curr, p) => {
                        const pickId = p.id.toString();

                        const existingPick = pickList.find((pick) => pick.id.toString() === pickId);
                        const existingPickState = existingPick && pickState[pickId];

                        if (!existingPickState) {
                            curr.push(EditBetActions.addPick({ pick: p }));
                        } else if (existingPickState.isRemoved) {
                            curr.push(EditBetActions.undoRemovePick({ pickId: p.id.toString() }));
                        }

                        return curr;
                    }, [] as Action[]);

                    actions = [
                        ...actions,
                        ...pickActions,
                        BetslipPickActions.addPicksHighlight({ payload: { pickIds, autoRestore: true } }),
                        BetslipPickActions.scrollToPick({ pickId: picks[0].id }),
                    ];
                    if (!this.adaptiveLayoutService.current.hasRightColumn) {
                        this.editBetPicksAddedInfoDialogService.show();
                    }
                }

                return actions;
            }),
        );
    });

    handleSwithToSwapSelectionEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.switchToSwapSelectionMode),
            map(() => {
                return UiManagerActions.addState({ state: UiStates.EditBetSwapSelection });
            }),
        );
    });

    handleCancelSwapSelectionEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.cancelSwapSelectionMode),
            map(() => {
                return UiManagerActions.removeState({ state: UiStates.EditBetSwapSelection });
            }),
        );
    });

    handleCancelAddingStakeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.cancelAddingStake),
            withLatestFrom(this.store.select(editBetStateSelector)),
            map(([action, editMyBetState]) => {
                const lastConfirmedValue = editMyBetState.confirmedStake || 0;

                return EditBetActions.confirmAddingStake({ stake: lastConfirmedValue });
            }),
        );
    });

    handlePickToSwapEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.startAddingPickToSwap),
            switchMap((action) => {
                const pick = action.pick;
                const request = this.betslipPicksRequestBuilder.buildRequestFromPickId([pick.id]);

                return from(this.bettingOffer.getPicks(request)).pipe(
                    filter((r) => !!r && !!r.fixtures.length),
                    mergeMap((response) => {
                        const fixture = response!.fixtures[0];
                        let pickId: V1PickId | V2OptionMarketPickId;
                        if (fixture.games.length) {
                            pickId = new V1PickId(fixture.id, fixture.optionMarkets[0].id, fixture.optionMarkets[0].options[0].id);
                        } else {
                            pickId = new V2OptionMarketPickId(
                                fixture.id,
                                fixture.optionMarkets[0].id,
                                fixture.optionMarkets[0].options[0].id,
                                fixture.optionMarkets[0].options[0].price.id,
                            );
                        }

                        const betslipPick = this.pickFactory.createPick(pickId, { source: '' }, fixture); // Event Model can be null because we don't support betbuilder in edit bet

                        if (betslipPick) {
                            return [EditBetActions.addPickToSwap({ pick: betslipPick })];
                        }

                        return [];
                    }),
                    catchError(() => []),
                );
            }),
        );
    });

    /**
     * Handle user swapped a pick action.
     * 1. Removes SWAP ui state (closes swap pick modal dialog).
     */
    handleConfirmSwapSelectionEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.swapPick),
            concatMap(function* (action: IPayloadAction<ISwapPickPayload>): Iterable<Action> {
                yield UiManagerActions.removeState({ state: UiStates.EditBetSwapSelection });
            }),
        );
    });

    /**
     * Handle starting adding pick to staging area.
     * If V2 pick shows confirm message.
     * Resolves the pick from bcd.
     * Adds the pick to staging area.
     */
    handleAddPickToContainerEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.startAddingPickToContainer),
            withLatestFrom(this.store.select(betslipSelector)),
            switchMap(([action, betslipState], index) => {
                if (betslipState.base.state !== BetslipState.Edit) {
                    // Do not add pick to add pick container if current state is different than edit.
                    return [];
                }

                const isFull = this.editMyBetCheckingService.isFull(betslipState.types.editBet);
                if (isFull) {
                    this.betslipDialogFullService.show(BetslipDialogFullMode.CannotAddMorePicks);

                    return [];
                }
                const pickId = action.pickId;
                const shouldAutoShowContainer = !betslipState.types.editBet.addPicksState.isVisible;
                const priceId = action.pickId instanceof V2OptionMarketPickId ? action.pickId.priceId : undefined;

                if (this.editBetV2PickAddConfirmationDialogService.shouldShow(pickId, betslipState)) {
                    return from(this.editBetV2PickAddConfirmationDialogService.show()).pipe(
                        filter((button) => button === ConfirmationDialogButtonType.Ok),
                        mergeMap(() => this.loadPickFromApi(pickId, shouldAutoShowContainer, priceId)),
                        catchError(() => []), // Ignore cancel clicking
                    );
                }

                return this.loadPickFromApi(pickId, shouldAutoShowContainer, priceId);
            }),
        );
    });

    handleSwapPickErrorEpicEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.handleSwapError),
            concatMap((action) => {
                return [
                    BetslipNotificationActions.setMessage({ payload: new SimpleBetslipNotification(action.error) }),
                    UiManagerActions.removeState({ state: UiStates.EditBetSwapSelection }),
                ];
            }),
        );
    });

    /**
     * Handle click on undo remove pick button.
     * Checks if betslip is full if not then do undo
     */
    handleStartUndoingRemovePickEffect$ = createEffect(() => {
        const _this = this;

        return this.actions$.pipe(
            ofType(EditBetActions.startUndoingRemovePick),
            withLatestFrom(this.store.select(editBetStateSelector)),
            // eslint-disable-next-line
            concatMap(function* ([action, editBetState]): Iterable<Action> {
                const isFull = _this.editMyBetCheckingService.isFull(editBetState);
                if (isFull) {
                    _this.betslipDialogFullService.show(BetslipDialogFullMode.CannotUndoRemovedPick);
                } else {
                    yield EditBetActions.undoRemovePick({ pickId: action.pickId });
                }
            }),
        );
    });

    /**
     * Handle Edit bet action.
     * When EPS value is changed check user EP value acceptance mode and if update matches auto update stake.
     */
    handleEditBetUpdateActionToAcceptEarlyPayoutEffect$ = createEffect(() => {
        const _this = this;

        return this.actions$.pipe(
            ofType(EditBetActions.editBetUpdated),
            concatLatestFrom(() => this.store.select(editBetStateSelector)),
            // eslint-disable-next-line
            concatMap(function* ([action, editBetState]): Iterable<Action> {
                const payload = action.payload;
                if (!!payload.earlyPayout && payload.earlyPayout.value > 0) {
                    // As ep value update and ep is enabled.
                    const acceptance = _this.userSettings.earlyPayoutAcceptance || EarlyPayoutAcceptance.Higher;
                    const acceptedStake = editBetState.acceptedEarlyPayoutStake || 0;
                    if (
                        acceptance === EarlyPayoutAcceptance.Any ||
                        (acceptance === EarlyPayoutAcceptance.Higher && acceptedStake < payload.earlyPayout.value)
                    ) {
                        yield EditBetActions.acceptCurrentEarlyPayoutValue();
                    }
                }
            }),
        );
    });

    /**
     * Handle BetslipEditBetActions.editBetUpdated action.
     * When early payout return that some picks are resulted, then update their resulted state.
     */
    handleEditBetUpdatedActionToResultPicksEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.editBetUpdated),
            withLatestFrom(this.store.select(betslipSelector)),
            concatMap(([action, betslipState]) => {
                const { earlyPayout } = action.payload;
                if (earlyPayout === null) {
                    return [];
                }

                const picks = betslipState.types.editBet.picks.pickList;
                const editBetPicks = betslipState.types.editBet.picks.pickState;
                const actions: IPayloadAction<IEditBetPickResultsPayload>[] = [];

                // Result all picks in resultedPicks collection.
                earlyPayout.resultedPicks.forEach((resultedPick) => {
                    const targetPick = picks
                        .filter((pick, index, array): pick is BetslipV1Pick => pick instanceof BetslipV1Pick)
                        .find((pick) => pick.option.id === resultedPick.optionId);
                    if (targetPick) {
                        const pickId = targetPick.id;
                        const currentState = editBetPicks[pickId.toString()].resultedState;
                        const newState = pickResultedStateFromEarlyPayoutResultedPickState(resultedPick.state);

                        if (currentState !== newState) {
                            const payload: IEditBetPickResultsPayload = {
                                pickId,
                                newState,
                                oldState: currentState,
                            };
                            actions.push(EditBetActions.applyEditBetPickResults({ payload }));
                        }
                    }
                });

                return actions;
            }),
        );
    });

    /**
     * Handle pick change status. When pick resulted status is updated.
     * Check the bet if all picks are resulted, if so show a information to the user and exit edit bet.
     */
    handleApplyEditBetPickResultsEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.applyEditBetPickResults),
            withLatestFrom(this.store.select(editBetStateSelector)),
            mergeMap(([action, editMyBetState]) => {
                const resultedState = getEditBetResultedState(editMyBetState);
                if (resultedState === EditBetPicksResultedState.Open) {
                    // All picks are open do nothing.
                    return [];
                }
                const dialogPromise = this.getResultedDialogPromise(resultedState);
                if (dialogPromise) {
                    // bet resulted show dialog and exit.
                    return from(dialogPromise).pipe(
                        map(() => EditBetActions.exitEditBet()),
                        catchError(() => []),
                    );
                }
                // eslint-disable-next-line no-bitwise
                if (resultedState & EditBetPicksResultedState.Won || resultedState & EditBetPicksResultedState.Canceled) {
                    // When some of the picks become won or cancelled show the message.
                    return [
                        BetslipErrorsActions.addPickError({
                            pickId: action.payload.pickId,
                            error: new ResultStateChanged(action.payload.pickId.toString(), action.payload.oldState, action.payload.newState),
                            betslipType: BetslipType.EditBet,
                        }),
                    ];
                }

                return [];
            }),
        );
    });

    /**
     * Handle DuplicatedEarlyPayout error from EPS push.
     * This error indicates that the cash out for this betslip was done (Auto cash out or done from the user in another session)
     */
    handleDuplicatedEarlyPayoutErrorEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.earlyPayoutUpdated),
            concatMap((action) => {
                const error = action.payload?.error;

                if (!error || error.type !== EarlyPayoutErrors.DuplicatedEarlyPayout) {
                    return [];
                }

                return from(this.editBetBetSettledDialogService.show()).pipe(map(() => EditBetActions.exitEditBet()));
            }),
        );
    });

    /**
     * When fill the edit bet with picks subscribe to them. When exit edit bet unsubscribe from all picks
     */
    schedulePicksForLiveCheckEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(EditBetActions.fillEditMyBet),
                tap((action) => {
                    const picks = values(action.payload.pickState);
                    const now = new Date();
                    const threshold = 7200000; // ignore picks with cutoff date more than 2 hours in future.
                    const preMatchPicks = picks
                        .filter(
                            (p) =>
                                BetslipV1Pick.isPick(p.pick) &&
                                !p.pick.isLive &&
                                p.state.resultedState === ResultStatus.Open &&
                                p.pick.resultStatus === ResultStatus.Open &&
                                0 < p.pick.event.cutOffDate.valueOf() - now.valueOf() && // Check only picks in the future. These picks are just loaded so
                                p.pick.event.cutOffDate.valueOf() - now.valueOf() < threshold,
                        )
                        .map((p) => p.pick) as BetslipV1Pick[];
                    this.refreshPickService.scheduleForCheck(preMatchPicks);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    private convertStartEditMyBet(action: IPayloadAction<EditMyBetPicksPayload>): IPayloadAction<IFillEditMyBetPayload> {
        const response = action.payload;
        const payload = this.editMyBetConvertersService.editMyBetPickToFilledMyBet(response);

        return EditBetActions.fillEditMyBet({ payload });
    }

    private loadPickFromApi(
        pickId: V1PickId | V2OptionMarketPickId | V2ParticipantPickId | V2OptionMarketXCastPickId,
        showAddPicksContainer: boolean,
        priceId: number | undefined,
    ): Observable<Action> {
        const service = this;
        const request = this.betslipPicksRequestBuilder.buildRequestFromPickId([pickId]);

        return from(this.bettingOffer.getPicks(request)).pipe(
            filter((fp) => Boolean(fp?.fixtures.length)),
            concatMap(function* (response: FixturePage | undefined): Iterable<Action> {
                const fixture = response!.fixtures[0];
                const isPriceBoosted = fixture.optionMarkets.some((x) => x.options.some((y) => y.boostedPrice?.id === priceId))!;

                if (fixture) {
                    const betslipPick = service.pickFactory.createPick(pickId, { source: 'editbet' }, fixture, priceId, isPriceBoosted); // Event Model can be null because we don't support betbuilder in edit bet
                    if (betslipPick) {
                        if (showAddPicksContainer) {
                            yield EditBetActions.switchToAddSelectionMode();
                        }
                        yield EditBetActions.addPickToContainer({ pick: betslipPick });
                    }
                }
            }),
        );
    }

    /**
     * Always show last added pick or staging area.
     *
     * @param state$ current app state.
     */
    private scrollToSummary(uiState: IUiState): void {
        const hasColumn = uiState.states.some((s) => s === UiStates.RightColumnVisible);
        if (hasColumn) {
            this.timerService.setTimeoutOutsideAngular(this.scrollColumnRightToSummaryContainerIfExisted, 0);
        }
    }

    private scrollColumnRightToSummaryContainerIfExisted(): void {
        const columnRigntElement = this._doc.querySelector('.column-right');
        const betslipEditBetSummaryContainerElement = this._doc.querySelector('.betslip-edit-bet__summary-container');
        if (columnRigntElement && betslipEditBetSummaryContainerElement) {
            columnRigntElement.scrollTop += betslipEditBetSummaryContainerElement.getBoundingClientRect().bottom - this._window.innerHeight;
        }
    }

    private getResultedDialogPromise(resultedState: EditBetPicksResultedState): Promise<ConfirmationDialogButtonType | undefined> | undefined {
        if (resultedState === EditBetPicksResultedState.Lost) {
            return this.editBetBetLostDialogService.show();
        }
        if (resultedState === EditBetPicksResultedState.Won) {
            return this.editBetBetWonDialogService.show();
        }
        if (resultedState === EditBetPicksResultedState.Canceled) {
            return this.editBetBetSettledDialogService.show();
        }

        return undefined;
    }

    private loadQuickDeposit(notEnoughError: NotEnoughMoney | undefined) {
        const stake = notEnoughError?.stake || 0;

        if (this.quickDepositService.isEnabled) {
            this.quickDepositService.disableQuickDepositLoader();
            this.quickDepositService.open(stake);

            return [];
        } else {
            return from(this.editBetExitConfirmationDialogService.show()).pipe(
                concatMap((val) => {
                    if (val === ConfirmationDialogButtonType.Ok) {
                        this.quickDepositService.disableQuickDepositLoader();

                        return [];
                    }

                    this.cashierService.goToCashierDeposit({});

                    return of(EditBetActions.exitEditBet());
                }),
            );
        }
    }
}
